<template>
  <div>
    <section v-if="modalVisible">
      <deal-form
        :deal-id="dealId"
        :is-read-only="isReadOnly"
        :modal-visible="modalVisible"
        @close="closeModal"
        @close-and-refresh="closeAndRefresh"
        @save-event="saveEvent"
        @create-new="closeAndOpenModalCreate"
        :defaultTradeDate="defaultTradeDate"
        :market-id="marketId"
      />
    </section>
    <section v-show="!modalVisible">
      <page-toolbar>
        <kendo-datepicker
          :value="asOfDate"
          :format="'yyyy-MM-dd'"
          style="width:120px"
          @change="asofDatePickerChanged"
        ></kendo-datepicker>
        <div v-if="!(isMaster || isReadOnly)">
          <kendo-button icon="add" title="Create deal" @click="openModalCreate"></kendo-button>
        </div>
        <responsible-email
          v-if="isMaster"
          v-model="responsibleEmail"
          @change="responsibleEmailChanged"
        />
      </page-toolbar>

      <kendo-grid
        height="calc(100vh - 170px)"
        ref="dealGrid"
        :editable="!isReadOnly && 'inline'"
        :dataSource="dataSource"
        :columns="columns"
        :pageable="true"
        :filterable="true"
        :sortable="true"
        :navigatable="true"
      ></kendo-grid>
    </section>

    <schedule-delivery-form
      v-if="scheduleModalVisible"
      :operation-id="operationId"
      :is-read-only="isReadOnly"
      :schedule-modal-visible="scheduleModalVisible"
      @close="closeScheduleDeliveryModal"
    />
  </div>
</template>
<script>
import $ from '@/helpers/jquery-bridge'
import DealForm from './DealForm'
import {mapGetters} from 'vuex'
import {enableExportExcel} from '@/helpers/export-helper'
import {getDataItem} from '@/helpers/kendo-helpers'
import PageToolbar from '@/components/PageToolbar'
import security from '@/mixins/security'
import categoryColumns from '@/mixins/categoryColumns'
import ScheduleDeliveryForm from './ScheduleDeliveryForm'
import ResponsibleEmail from '@/components/ResponsibleEmail'

export default {
  name: 'DealView',
  mixins: [security, categoryColumns],
  components: {
    DealForm,
    PageToolbar,
    ScheduleDeliveryForm,
    ResponsibleEmail,
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.dealLinkClick)
    this.$refs.dealGrid.$el.removeEventListener('click', this.openScheduleDelivery)
  },
  mounted() {
    document.body.addEventListener('click', this.dealLinkClick, false)
    let grid = this.$refs.dealGrid.kendoWidget()
    enableExportExcel(grid, '/api/list/deal/exportToExcel', $('.page_toolbar-item'))
    this.$refs.dealGrid.$el.addEventListener('click', this.openScheduleDelivery, false)
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({marketId: 'app/marketId'}),
    isMaster() {
      return this.clientId == 0
    },
    grid() {
      return this.$refs.dealGrid
    },
    endDate() {
      return kendo.toString(this.asOfDate, 'yyyy-MM-dd')
    },
    columns() {
      return [
        {
          template: dataItem => {
            if (dataItem.OperationId === 0) {
              return ''
            }
            var template
            if (dataItem.InstructionId === -1) {
              var checkedAttr = this.checkedItems[dataItem.OperationId] ? 'checked="checked"' : ''
              template =
                '<input style="display: inline-block;" id="checkBoxId' +
                dataItem.OperationId +
                '" type="checkbox" class="checkbox" ' +
                checkedAttr +
                ' />'
            } else {
              template = '<a class="instructionHref k-cellLink" href="javascript:;">T</a>'
            }
            return template
          },
          headerTemplate: () => {
            if (!this.isReadOnly) return ''
            var fxInstruction =
              "<button type='button' id='fxInstruction' title='FX Instruction' class='btn btn-xs btn-default'>" +
              "   <i class='glyphicon glyphicon-align-left'></i>" +
              '</button>'
            return (
              fxInstruction +
              '<input style="display:inline-block" type="checkbox" class="checkAll checkbox" />'
            )
          },
          attributes: {style: 'text-align: center;'},
          width: 50,
          hidden: true,
        },
        {
          field: 'dealId',
          title: 'Group Id',
          width: 100,
          locked: true,
          attributes: {style: 'text-align: right;'},
          filterable: {
            ui: function(element) {
              element.kendoNumericTextBox({format: 'N0', decimals: 0, spinners: false})
            },
          },
        },
        {
          field: 'dealCode',
          title: 'Number',
          width: 100,
          attributes: {style: 'text-align: left;'},
          locked: true,
          template: kendo.template(
            '<a href="javascript:;" class="dealCodeLink k-cellLink">#=dealCode#</a>',
          ),
        },
        {
          field: 'clientName',
          title: 'Client',
          width: 200,
          attributes: {style: 'text-align: left;'},
          hidden: !this.isMaster,
        },
        {
          field: 'sectorName',
          title: 'Sector',
          width: 120,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'divisionName',
          title: 'Division',
          width: 150,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'counterpartyCode',
          title: 'Counterparty',
          width: 150,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'reference',
          title: 'Reference',
          width: 142,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'otherReference',
          title: 'Others/Premium',
          width: 142,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'operationDescription',
          title: 'Description',
          width: 350,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'productCode',
          title: 'Product',
          width: 100,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'legCode',
          title: 'Leg',
          width: 80,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'tradeDate',
          title: 'Trade Date',
          width: 120,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
        },
        {
          field: 'startDate',
          title: 'Start Date',
          width: 120,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
        },
        {
          field: 'hedgeMonth',
          title: 'Hedge Month',
          width: 120,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
        },
        {
          field: 'maturityDate',
          title: 'Maturity Date',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
        },
        {
          field: 'settlementDate',
          title: 'Settlement Date',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd}',
        },
        {
          field: 'buySell',
          title: 'Buy / Sell',
          width: 82,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'unsettlementAmount',
          title: 'Undelivered Amount',
          width: 140,
          attributes: {style: 'text-align: right;'},
          template: function(item) {
            var amount = kendo.toString(item.unsettlementAmount, 'n0')
            var template =
              item.legCode === 'FWD'
                ? '<a title="Delivery Schedule" data-operationid="' +
                  item.operationId +
                  '" href="javascript:;" class="deliveryScheduleLink k-cellLink">' +
                  amount +
                  '</a>'
                : '<span>' + amount + '</span>'
            return template
          },
        },
        {
          title: 'Quantity Com.Ccy',
          field: 'foreignAmount',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n0}',
        },
        {
          field: 'foreignCcy',
          title: 'Com.Ccy',
          width: 75,
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Hedge Rate',
          field: 'hedgeRate',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n4}',
        },
        {
          field: 'sellBuy',
          title: 'Buy / Sell',
          width: 82,
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Amount Ccy2',
          field: 'baseAmount',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n0}',
        },
        {
          title: 'Ccy2',
          field: 'baseCcy',
          width: 75,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'hedgeType',
          title: 'Hedge Type',
          width: 100,
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Reference Rate',
          field: 'referenceRate',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n4}',
        },
        {
          title: 'Forward Points',
          field: 'forwardPoints',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n4}',
        },
        {
          field: 'gainLoss',
          title: 'Gain / Loss',
          width: 100,
          attributes: {style: 'text-align: right;'},
          format: '{0:n2}',
        },
        {
          field: 'economySpread',
          title: 'Savings FINMETRIX',
          width: 100,
          attributes: {style: 'text-align: right;'},
          format: '{0:n2}',
        },
        {
          field: 'exercised',
          title: 'Exercised',
          width: 100,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'optionTypeName',
          title: 'Option Type',
          width: 110,
          attributes: {style: 'text-align: left;'},
        },
        {
          title: 'Other Barrier',
          field: 'collarRate',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:n4}',
        },
        {
          field: 'settlementMethodName',
          title: 'Settlement Method',
          width: 110,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'settlementCcy',
          title: 'Settlement Ccy',
          width: 110,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'statusName',
          title: 'Status',
          width: 100,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'comment',
          title: 'Comment',
          width: 182,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'lastModified',
          title: 'Last Modified',
          width: 140,
          attributes: {style: 'text-align: right;'},
          format: '{0:yyyy-MM-dd HH:mm}',
        },
        {
          field: 'lastUser',
          title: 'Last User',
          width: 120,
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'responsibleEmail',
          title: 'Responsible',
          width: 232,
          attributes: {style: 'text-align: left;'},
          hidden: !this.isMaster,
        },
        {
          field: 'managerEmail',
          title: 'Manager',
          width: 232,
          attributes: {style: 'text-align: left;'},
          hidden: !this.isMaster,
        },
        {
          field: 'canDelete',
          title: '&nbsp;',
          width: 40,
          filterable: false,
          attributes: {style: 'text-align: center;'},
          template:
            "# if(canDelete) { # <a class='k-button k-button-icontext k-grid-delete' >" +
            "<span class='k-icon k-i-delete'></span>Delete</a> # } #",
          //hidden: !this.editable,
          hidden: true,
        },
        ...this.categoryColumns,
      ]
    },
    defaultTradeDate() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today.toISOString().slice(0, 10)
    },
  },
  methods: {
    dealLinkClick({target}) {
      if (!/dealCodeLink/.test(target.className)) return
      const dataItem = getDataItem(target)
      this.modalVisible = true
      this.dealId = dataItem.dealId
    },
    openModalUpdate: function(e) {
      e.preventDefault()

      let grid = this.$refs.dealGrid.kendoWidget()
      var dataItem = grid.dataItem($(e.currentTarget).closest('tr'))
      this.modalVisible = true
      this.dealId = dataItem.dealId
    },
    closeAndOpenModalCreate() {
      this.closeModal()

      setTimeout(() => {
        this.openModalCreate()
      }, 500)
    },
    openModalCreate() {
      this.modalVisible = true
      this.dealId = 0
    },
    async closeModal() {
      this.dealId = null
      this.modalVisible = false

      await this.getGroupConfig()
      await this.gridRefresh()
    },
    asofDatePickerChanged(e) {
      this.asOfDate = e.sender.value()
      this.gridRefresh()
    },
    responsibleEmailChanged() {
      this.gridRefresh()
    },
    gridRefresh() {
      let grid = this.$refs.dealGrid.kendoWidget()
      return grid.dataSource.read()
    },
    saveEvent(dealId) {
      this.dealId = dealId
      //let grid = this.$refs.dealGrid.kendoWidget()
      //grid.dataSource.read()
    },
    closeAndRefresh() {
      this.closeModal()
      this.refreshCategoryColumns().then(() => {
        this.gridRefresh()
      })
    },
    openScheduleDelivery({target}) {
      if (!/deliveryScheduleLink/.test(target.className)) return
      const dataItem = getDataItem(target)
      this.operationId = dataItem.operationId
      this.scheduleModalVisible = true
    },
    closeScheduleDeliveryModal() {
      this.operationId = null
      this.scheduleModalVisible = false
    },
  },
  data() {
    return {
      dealId: null,
      modalVisible: false,
      checkedItems: [],
      asOfDate: new Date(),
      responsibleEmail: 'ALL',
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 20,
        transport: {
          read: {
            type: 'GET',
            url: '/api/list/deal',
            data: () => ({
              endDate: this.endDate,
              clientId: this.clientId,
              marketId: this.marketId,
              responsibleEmail: this.responsibleEmail,
            }),
            dataType: 'json',
          },
          create: {
            type: 'POST',
            url: '/api/list/deal',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/list/deal',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/list/deal',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'dealId',
            fields: {
              dealId: {type: 'number'},
              ccyPairId: {type: 'number'},
              operationId: {type: 'number'},
              instructionId: {type: 'number'},
              dealCode: {type: 'string'},
              reference: {type: 'string'},
              clientName: {type: 'string'},
              responsibleEmail: {type: 'string'},
              managerEmail: {type: 'string'},
              statusName: {type: 'string'},
              sectorName: {type: 'string'},
              counterpartyCode: {type: 'string'},
              divisionName: {type: 'string'},
              productCode: {type: 'string'},
              buySell: {type: 'string'},
              baseAmount: {type: 'number'},
              baseCcy: {type: 'string'},
              tradeDate: {type: 'date'},
              startDate: {type: 'date'},
              hedgeMonth: {type: 'date'},
              maturityDate: {type: 'date'},
              settlementDate: {type: 'date'},
              foreignAmount: {type: 'number'},
              unsettlementAmount: {type: 'number'},
              foreignCcy: {type: 'string'},
              hedgeRate: {type: 'number'},
              referenceRate: {type: 'number'},
              collarRate: {type: 'number'},
              description: {type: 'string'},
              hedgeType: {type: 'string'},
              lastModified: {type: 'date'},
              lastUser: {type: 'string'},
              exercised: {type: 'string'},
              gainLoss: {type: 'number'},
              economySpread: {type: 'number'},
              optionTypeName: {type: 'string'},
              settlementMethodName: {type: 'string'},
              settlementCcy: {type: 'string'},
              comment: {type: 'string'},
              canDelete: {type: 'bool', defaultValue: true, editable: false},
            },
          },
        },
      }),
      scheduleModalVisible: false,
      operationId: null,
    }
  },
}
</script>
